import {Component, EventEmitter, Input, Output} from '@angular/core';
import {
  ClassificatorValuesQuery,
  FilterModel,
  GLOBAL,
  LanguagesQuery,
  LeModalService,
  PropertiesQuery,
  TableComponent
} from "common-shared";
import {BehaviorSubject, debounceTime, distinctUntilChanged} from "rxjs";
import {ActionBarService} from "web-shared";
import {map, takeUntil} from "rxjs/operators";
import {LazyLoadEvent} from "primeng/api";
import {ResourceEditModalComponent} from "../resource-edit-modal/resource-edit-modal.component";
import {ResourceListFilter, ResourceListQuery, ResourceQuery} from '../models/resource.query';
import {ResourceService} from "../resource.service";
import {ResourcesQuery} from "../resource.store";

@Component({
  selector: 'app-resource-table',
  templateUrl: './resource-table.component.html',
  styles: []
})
export class ResourceTableComponent extends TableComponent {
  @Input() filter: FilterModel<ResourceListFilter>;
  @Input() stateKey: string = 'resource-list-state';
  filteredData$: BehaviorSubject<ResourceListQuery[]> = new BehaviorSubject<ResourceListQuery[]>([]);
  selectedResource: ResourceListQuery | ResourceListQuery[];
  resourceListPropertyId = GLOBAL.Properties.ResourceListFilterSettings;
  @Input() selectionMode: 'single' | 'multiple' = 'single';
  @Input() selectedId: string | string[];
  @Output() onSelect = new EventEmitter<string | string[]>();

  constructor(public classificatorValueService: ClassificatorValuesQuery, public actionBarService: ActionBarService, private resourceService: ResourceService, public resourcesQuery: ResourcesQuery, private modalService: LeModalService, public propertiesQuery: PropertiesQuery, public languagesQuery: LanguagesQuery) {
    super();
  }

  ngOnInit() {
    this.search$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      if (this.table) {
        this.table.filterGlobal(value, 'contains');
      }
    });
    this.lazyLoadStream.pipe(takeUntil(this.componentDestroyed), debounceTime(this.loadDebounceTime), distinctUntilChanged(), map((value) => {
      this.loadDebounceTime = 0;
      return value;
    })).subscribe(event => this.load(event));
    this.applyDefaultFilters();
  }

  load(event: LazyLoadEvent = null) {
    this.prepareLoad(this.filter, event);
    this.resourceService.getByFilter(this.filter).pipe(takeUntil(this.componentDestroyed)).subscribe(pagedData => {
      this.filteredData$.next(pagedData.Data);
      this.updateAfterLoad(this.filter, pagedData);
      this.ensureSelection(pagedData.Data);
    });
  }

  open(row: ResourceQuery) {
    this.modalService.show(ResourceEditModalComponent, {initialState: {entityId: row.Id} as ResourceEditModalComponent, class: 'modal-xl'});
  }

  clearState() {
    this.table.clearState();
    this.filter.Data = new ResourceListFilter();
    this.filter.Global = null;
    this.applyDefaultFilters();
    setTimeout(() => {
      this.tryLazyLoad();
    });
  }

  loadSavedState(state: LazyLoadEvent) {
    this.filter.Data = new ResourceListFilter();
    this.table.clearState();
    this.table.filters = state.filters ?? {};
    this.onStateRestore(state);
    const doesNotTriggerLazyLoad = this.tableSortOrder === state.sortOrder && this.tableSortField === state.sortField;
    if (doesNotTriggerLazyLoad) {
      this.load(state);
    }
    this.table.saveState();
  }

  onStateRestore(state) {
    state.selection = null;

    setTimeout(() => {
      this.tableSortField = state.sortField ?? this.tableSortField ?? this.sortField;
      this.tableSortOrder = state.sortOrder ?? this.tableSortOrder ?? this.sortOrder;
      this.hasFilters = FilterModel.hasAny(state.filters);
      if (this.hasFilters) {
        this.filter.AdvancedFilter = true;
      }
    });
  }

  onTableDropdownOpen(isOpenEvent: boolean) {
    if (isOpenEvent) {
      this.reloadOnClose = false;
    } else {
      this.tableDropdownClosed.emit();
      if (this.reloadOnClose) {
        this.tryLazyLoad();
      }
    }
  }

  onRowSelect() {
    if (this.selectionMode == 'single') {
      this.onSelect.emit((this.selectedResource as ResourceListQuery).Id)
    } else {
      this.onSelect.emit([...(this.selectedResource as ResourceListQuery[]).map(value => value.Id)])
    }
  }

  tryLazyLoad() {
    if (typeof this.table.filter === 'function') {
      this.table._filter();
    } else {
      this.load();
    }
  }

  private ensureSelection(items: ResourceListQuery[]) {
    if (this.selectionMode == 'single') {
      if (this.selectedId) {
        this.selectedResource = items.find(value => value.Id == this.selectedId);
      }
    } else {
      let selectedIds = this.selectedId as string[];
      this.selectedResource = [...items.filter(value => selectedIds.includes(value.Id))];
    }
  }
}
